import React, { useState, useEffect } from "react";
import { getAllSettings, updateSetting } from "../api/settings/settingsRequest";
import { SETTINGS_FR } from "../utils/constants"; // Import the French settings map
import { Input, Button, Spin, Alert, Typography, Space } from "antd";

const { Title, Text } = Typography;

const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [saving, setSaving] = useState({}); // Track saving state per key
  const [settingsData, setSettingsData] = useState({}); // State to hold setting values directly

  // Define the keys we want to manage based on SETTINGS_FR
  const settingKeysToShow = Object.keys(SETTINGS_FR);

  // Effect 1: Fetch data on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccessMessage("");
        setSettingsData({}); // Reset data on fetch
        const fetchedSettings = await getAllSettings();
        console.log("Fetched Settings:", fetchedSettings);

        const processedValues = {};
        settingKeysToShow.forEach((key) => {
          processedValues[key] = fetchedSettings.hasOwnProperty(key)
            ? fetchedSettings[key] ?? ""
            : "";
        });
        console.log("Processed Values:", processedValues);

        setSettingsData(processedValues); // Set the state directly
      } catch (err) {
        setError("Échec du chargement des paramètres.");
        console.error(err);
        setSettingsData({}); // Set empty object on error
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Handle input changes directly
  const handleInputChange = (key, value) => {
    setSettingsData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async (key) => {
    // Basic validation: Check if the field is empty (optional)
    const value = settingsData[key];
    if (value === undefined || value === null || value === "") {
      setError(`Le champ '${SETTINGS_FR[key]}' ne peut pas être vide.`);
      return;
    }

    try {
      setSaving((prev) => ({ ...prev, [key]: true }));
      setError(null);
      setSuccessMessage("");
      // Get value directly from state
      await updateSetting(key, value);
      setSuccessMessage(
        `Paramètre '${SETTINGS_FR[key]}' mis à jour avec succès.`
      );
    } catch (errInfo) {
      setError(
        `Échec de la mise à jour du paramètre '${SETTINGS_FR[key]}'.`
      );
      console.error(errInfo);
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 pt-8 max-w-2xl">
      <Title level={2} className="mb-6 text-center">
        Paramètres de l'application
      </Title>
      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          className="mb-4"
          closable
          onClose={() => setError(null)}
        />
      )}
      {successMessage && (
        <Alert
          message={successMessage}
          type="success"
          showIcon
          className="mb-4"
          closable
          onClose={() => setSuccessMessage("")}
        />
      )}
      <div className="space-y-4">
        {settingKeysToShow.map((key) => (
          <div key={key}>
            <label className="font-semibold block mb-1">
              {SETTINGS_FR[key]}
            </label>
            <Space.Compact className="w-full">
              <Input
                type="text"
                placeholder={`Entrez la valeur pour ${SETTINGS_FR[key]}`}
                className="flex-grow"
                value={settingsData[key] || ""}
                onChange={(e) => handleInputChange(key, e.target.value)}
                disabled={saving[key]}
              />
              <Button
                type="primary"
                onClick={() => handleSave(key)}
                loading={saving[key]}
              >
                Enregistrer
              </Button>
            </Space.Compact>
          </div>
        ))}

        {settingKeysToShow.length === 0 && !error && (
          <Text>Aucun paramètre à afficher trouvé dans les constantes.</Text>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
